import { GRAY_900, YELLOW_300 } from 'constants/styling/theme'
import { MeasurementInfoCard, measurementInfoCardIconSx } from 'components/common/MeasurementInfoCard'

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { MEASUREMENT_CANCELLATION_FLAT_FEE_EUR } from 'constants/purchaseFlow'
import { MUIDivider } from 'components/common/MUIDivider'
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

/**
 * @component
 * Info cards shown under the measurement cost calculation notice on the order review step —
 * technician call, delivery time (standard/express) and the cancellation policy.
 *
 * @example
 * <MeasurementReviewInfo />
 */
export const MeasurementReviewInfo: React.FC = () => {
  const { t } = useTranslation(['purchase_flow'])

  const iconSx = measurementInfoCardIconSx

  return (
    <Stack gap={1} data-testid="measurement-review-info">

      <MeasurementInfoCard
        icon={<PhoneInTalkOutlinedIcon sx={iconSx} />}
        title={t('measurement_step.review_info.technician_call.title')}
        hint={t('measurement_step.review_info.technician_call.hint')}
      >
        <Typography variant="text-sm" color={GRAY_900}>
          {t('measurement_step.review_info.technician_call.text')}
        </Typography>
      </MeasurementInfoCard>

      <MeasurementInfoCard
        icon={<AccessTimeOutlinedIcon sx={iconSx} />}
        title={t('measurement_step.review_info.delivery_time.title')}
        hint={t('measurement_step.review_info.delivery_time.hint')}
      >
        <Stack gap={1}>

          <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
            {t('measurement_step.review_info.delivery_time.standard_title')}
          </Typography>

          <Typography variant="text-sm" color={GRAY_900}>
            {t('measurement_step.review_info.delivery_time.standard_text')}
          </Typography>

          <MUIDivider margin={12} color={YELLOW_300} />

          <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
            {t('measurement_step.review_info.delivery_time.express_title')}
          </Typography>

          <Typography variant="text-sm" color={GRAY_900}>
            {t('measurement_step.review_info.delivery_time.express_text')}
          </Typography>

          <MUIDivider margin={12} color={YELLOW_300} />

        </Stack>
      </MeasurementInfoCard>

      <MeasurementInfoCard
        icon={<CancelOutlinedIcon sx={iconSx} />}
        title={t('measurement_step.review_info.cancellation_policy.title')}
        hint={t('measurement_step.review_info.cancellation_policy.hint')}
      >
        <Typography variant="text-sm" color={GRAY_900}>
          {/* Mila's proposal: a flat 100 € fee instead of 75% of the order value — measurement only
            * (this card only mounts for a measurement product); the photo/shooting cancellation copy elsewhere is untouched. Enforcement: ISSUE-3019. */}
          {t('measurement_step.review_info.cancellation_policy.text_flat_fee', { fee: MEASUREMENT_CANCELLATION_FLAT_FEE_EUR })}
        </Typography>
      </MeasurementInfoCard>

    </Stack>
  )
}
